/**
 * `PlaylistSlideRow` — linha sortable da sequência da playlist (TH-53378).
 *
 * Cada slide expõe: thumb, nome do produto/arte, input de duração
 * (segundos, 1–60), toggle ativo e botão remover. Drag handle dedicado
 * (ícone GripVertical) impede que cliques nos inputs disparem reorder.
 *
 * Para slides com `custom_art_id`, o backend NÃO devolve `product_art`
 * aninhado — a UI passa `customArt` resolvido client-side via lookup
 * na lista de custom_arts. Quando não conseguimos resolver (arte
 * apagada, fora do escopo do filtro atual), mostramos placeholder com
 * o ID encurtado.
 */

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, ImageIcon, Trash2 } from "lucide-react";

import { Input } from "@/components/ui/Input";
import { cn } from "@/lib/utils";
import type { CustomArtRead } from "@/hooks/useCreateCustomArt";
import type { PlaylistSlideWithArt } from "@/hooks/useTVPlaylist";

export interface DraftSlide extends PlaylistSlideWithArt {
  /**
   * Slides recém-adicionados na UI ainda não têm id no servidor; usamos
   * um id local `local:<uuid>` pra dnd-kit e React keys. Backend ignora
   * — o PUT só repassa product/custom_art_id+posição+duração.
   */
  _localId: string;
}

export interface PlaylistSlideRowProps {
  slide: DraftSlide;
  index: number;
  customArt?: CustomArtRead;
  onDurationChange: (localId: string, durationMs: number) => void;
  onActiveToggle: (localId: string) => void;
  onRemove: (localId: string) => void;
}

const MIN_SECONDS = 1;
const MAX_SECONDS = 60;

export function PlaylistSlideRow({
  slide,
  index,
  customArt,
  onDurationChange,
  onActiveToggle,
  onRemove,
}: PlaylistSlideRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: slide._localId });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const thumbUrl = slide.product_art?.art_url ?? customArt?.art_url ?? null;
  const isWebUrl = thumbUrl ? /^https?:\/\//i.test(thumbUrl) : false;
  const label =
    slide.product_art?.product_name ??
    customArt?.name ??
    (slide.custom_art_id
      ? `Arte personalizada (${slide.custom_art_id.slice(0, 8)})`
      : "Arte sem referência");

  const durationSeconds = Math.round(slide.duration_ms / 1000);

  function handleDurationInput(e: React.ChangeEvent<HTMLInputElement>) {
    const raw = Number(e.target.value);
    if (!Number.isFinite(raw)) return;
    const clamped = Math.min(MAX_SECONDS, Math.max(MIN_SECONDS, Math.round(raw)));
    onDurationChange(slide._localId, clamped * 1000);
  }

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-center gap-3 rounded-md border border-[var(--color-border)] bg-[var(--color-background)] p-2",
        isDragging && "z-10 shadow-lg ring-1 ring-[var(--color-primary)]",
        !slide.active && "opacity-60",
      )}
    >
      <button
        type="button"
        aria-label="Arrastar pra reordenar"
        className="cursor-grab rounded p-1 text-[var(--color-muted-foreground)] hover:bg-[var(--color-secondary)]"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" aria-hidden="true" />
      </button>

      <span className="w-6 text-center font-mono text-xs tabular-nums text-[var(--color-muted-foreground)]">
        {index + 1}
      </span>

      <div className="flex h-12 w-20 shrink-0 items-center justify-center overflow-hidden rounded bg-[var(--color-card)]">
        {isWebUrl && thumbUrl ? (
          <img
            src={thumbUrl}
            alt={label}
            className="h-full w-full object-cover"
            loading="lazy"
            draggable={false}
          />
        ) : (
          <ImageIcon
            className="h-5 w-5 text-[var(--color-muted-foreground)]"
            aria-hidden="true"
          />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-[var(--color-foreground)]">
          {label}
        </p>
        <p className="truncate text-[10px] uppercase tracking-wide text-[var(--color-muted-foreground)]">
          {slide.product_art_id ? "Arte IA" : "Arte personalizada"}
        </p>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <label className="flex items-center gap-1 text-xs text-[var(--color-muted-foreground)]">
          <span className="font-mono tabular-nums">Duração</span>
          <Input
            type="number"
            min={MIN_SECONDS}
            max={MAX_SECONDS}
            step={1}
            value={durationSeconds}
            onChange={handleDurationInput}
            aria-label="Duração em segundos"
            className="h-8 w-16 px-2 font-mono tabular-nums"
          />
          <span className="font-mono">s</span>
        </label>

        <label className="flex shrink-0 cursor-pointer items-center gap-1 text-xs text-[var(--color-muted-foreground)]">
          <input
            type="checkbox"
            checked={slide.active}
            onChange={() => onActiveToggle(slide._localId)}
            aria-label={`Toggle ativo do slide ${index + 1}`}
            className="h-4 w-4 cursor-pointer accent-[var(--color-primary)]"
          />
          Ativo
        </label>

        <button
          type="button"
          onClick={() => onRemove(slide._localId)}
          aria-label={`Remover slide ${index + 1}`}
          className="rounded p-1 text-[var(--color-muted-foreground)] hover:bg-[var(--color-destructive)]/10 hover:text-[var(--color-destructive)]"
        >
          <Trash2 className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </li>
  );
}
